import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import { ChevronDown, Eye, X, RotateCw } from 'lucide-react';
import DateRangePicker from '../../../components/DateRangePicker';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const base = isLocal ? 'http://localhost:8080' : '';
  if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
  return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const ClaimRequestsTab = ({ toast, setLightboxImage }) => {
  const { hasPermission } = useAuth();

  // Claims state
  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [claimsPage, setClaimsPageState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('admin_page_claim_requests');
      return saved ? Math.max(0, parseInt(saved, 10)) : 0;
    } catch (e) {
      return 0;
    }
  });

  const setClaimsPage = useCallback((valOrFn) => {
    setClaimsPageState(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        sessionStorage.setItem('admin_page_claim_requests', String(next));
      } catch (e) {}
      return next;
    });
  }, []);

  const [claimsPageSize, setClaimsPageSize] = useState(10);
  const [claimsTotalPages, setClaimsTotalPages] = useState(0);
  const [claimsTotalItems, setClaimsTotalItems] = useState(0);
  const [claimFilters, setClaimFilters] = useState({ startDate: '', endDate: '' });
  
  // Claim action states
  const [claimActionLoading, setClaimActionLoading] = useState(false);
  const [showRejectClaimModal, setShowRejectClaimModal] = useState(false);
  const [claimRejectingId, setClaimRejectingId] = useState(null);
  const [claimRejectReason, setClaimRejectReason] = useState('');

  // Selected claim modal states
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [selectedClaimListing, setSelectedClaimListing] = useState(null);
  const [selectedClaimListingLoading, setSelectedClaimListingLoading] = useState(false);

  const fetchClaims = useCallback(async () => {
    if (!hasPermission('APPROVE_LISTINGS')) return;
    setClaimsLoading(true);
    try {
      const res = await vendorService.getAdminClaims(
        claimsPage,
        claimsPageSize,
        'ALL',
        claimFilters.startDate,
        claimFilters.endDate
      );
      if (res.success && res.data) {
        setClaims(res.data.claims || []);
        setClaimsTotalPages(res.data.totalPages || 0);
        setClaimsTotalItems(res.data.totalItems || 0);
      } else {
        toast.error(res.error || "Failed to fetch claims.");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while fetching claims.");
    } finally {
      setClaimsLoading(false);
    }
  }, [claimsPage, claimsPageSize, hasPermission, toast, claimFilters.startDate, claimFilters.endDate]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const handleClaimStatusUpdate = async (claimId, status, rejectReason = null) => {
    setClaimActionLoading(true);
    try {
      const res = await vendorService.updateClaimStatus(claimId, status, rejectReason);
      if (res.success) {
        toast.success(`Claim request successfully ${status.toLowerCase()}ed!`);
        setShowRejectClaimModal(false);
        setClaimRejectReason('');
        setClaimRejectingId(null);
        fetchClaims();
      } else {
        toast.error(res.error || `Failed to update claim status.`);
      }
    } catch (err) {
      toast.error("An error occurred while updating claim status.");
      console.error(err);
    } finally {
      setClaimActionLoading(false);
    }
  };

  const handleViewClaimDetails = async (claim) => {
    setSelectedClaim(claim);
    setSelectedClaimListing(null);
    setSelectedClaimListingLoading(true);
    try {
      const res = await vendorService.getPublicListingById(claim.listing_id);
      if (res.success && res.data) {
        setSelectedClaimListing(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch listing details for claim comparison:", err);
    } finally {
      setSelectedClaimListingLoading(false);
    }
  };

  const handleCloseClaimDetails = () => {
    setSelectedClaim(null);
    setSelectedClaimListing(null);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-6 space-y-6 text-left">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-2 select-none border-b border-gray-100 pb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Claim Requests Registry</h2>
          <p className="text-xs text-gray-400 mt-1 font-semibold">Review claims made by business owners on imported unclaimed listings.</p>
        </div>

        {/* Date Filter & Total Claims */}
        <div className="flex flex-wrap items-center gap-3 lg:justify-end">
          {/* Date Filter Inputs */}
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 p-1.5 px-3 rounded-xl select-none">
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">DATE:</span>
            <DateRangePicker
              startDate={claimFilters.startDate}
              endDate={claimFilters.endDate}
              onChange={(start, end) => {
                setClaimFilters({ startDate: start, endDate: end });
                setClaimsPage(0);
              }}
              placeholder="Select Date Range"
              className="!w-48 text-xs"
            />
            {(claimFilters.startDate || claimFilters.endDate) && (
              <button
                onClick={() => {
                  setClaimFilters({ startDate: '', endDate: '' });
                  setClaimsPage(0);
                }}
                className="px-2 py-0.5 text-[10px] font-semibold text-red-650 hover:bg-red-50 border border-red-200/50 rounded-lg transition-colors cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>

          <span className="text-xs text-gray-500 font-bold uppercase tracking-wider bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-xl whitespace-nowrap select-none">
            Total claims: {claimsTotalItems}
          </span>
        </div>
      </div>

      {claimsLoading ? (
        <div className="flex flex-col justify-center items-center py-20 select-none">
          <div className="w-8 h-8 border-3 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
          <span className="text-xs text-gray-400 mt-3 font-semibold">Fetching claims...</span>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse">
              <thead>
                <tr className="bg-gray-55 border-b border-gray-100 text-left text-xs font-bold text-gray-400 uppercase tracking-wider text-left">
                  <th className="py-4 px-5 pl-6">Listing Details</th>
                  <th className="py-4 px-5">Claimant Details</th>
                  <th className="py-4 px-5">Date Submitted</th>
                  <th className="py-4 px-5">Proof & Comments</th>
                  <th className="py-4 px-5">Status</th>
                  <th className="py-4 px-5 pr-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
                {claims.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-12 text-center text-gray-400 font-semibold">
                      No claim requests found in database.
                    </td>
                  </tr>
                ) : (
                  claims.map((claim) => (
                    <tr key={claim.id} className="hover:bg-gray-55/50 transition-all border-b border-gray-100 last:border-0">
                      <td className="py-4 px-5 pl-6 text-left">
                        <div className="font-bold text-gray-800 text-sm hover:text-[#2ab391] transition-colors">{claim.companyName}</div>
                        <div className="inline-flex items-center gap-1.5 mt-1 text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          ID: {claim.listing_id}
                        </div>
                      </td>
                      <td className="py-4 px-5 text-left">
                        <div className="font-semibold text-gray-850 text-sm">{claim.claimantName || 'N/A'}</div>
                        <div className="text-[11px] text-gray-505 font-medium">{claim.claimantDesignation || 'N/A'}</div>
                        <div className="text-[11px] text-gray-400 mt-1 font-sans">
                          {claim.claimantPhone || 'N/A'} • <span className="break-all">{claim.user_email}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5 text-left text-xs text-gray-550">
                        <div className="font-semibold text-gray-700">
                          {new Date(claim.created_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {new Date(claim.created_at).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </td>
                      <td className="py-4 px-5 text-left">
                        <div className="flex flex-col gap-1.5 items-start">
                          {claim.proofPhotoUrl ? (
                            <a
                              href={getImageUrl(claim.proofPhotoUrl)}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-750 bg-indigo-50 border border-indigo-150 px-2 py-0.5 rounded hover:bg-indigo-100 transition-colors shadow-2xs"
                            >
                              Govt Proof Document
                            </a>
                          ) : (
                            <span className="text-[10px] font-semibold text-gray-400 bg-gray-50 px-2 py-0.5 rounded border border-gray-200">No Proof</span>
                          )}
                          {claim.comments ? (
                            <div className="text-xs text-gray-500 max-w-[200px] truncate italic" title={claim.comments}>
                              "{claim.comments}"
                            </div>
                          ) : (
                            <span className="text-[11px] text-gray-400 italic">No comment</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-5 text-left">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase border ${claim.status === "PENDING"
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : claim.status === "APPROVED"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-rose-50 text-rose-700 border-rose-200"
                          }`}>
                          {claim.status}
                        </span>
                        {claim.status === "REJECTED" && claim.reject_reason && (
                          <p className="text-[10px] text-rose-500 font-bold mt-1 max-w-[150px] truncate" title={claim.reject_reason}>
                            Reason: {claim.reject_reason}
                          </p>
                        )}
                      </td>
                      <td className="py-4 px-5 pr-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewClaimDetails(claim)}
                            className="text-gray-650 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 border border-gray-200 py-1.5 px-2.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 active:scale-95 cursor-pointer shadow-2xs"
                            title="View Comparison and Proof Details"
                          >
                            <Eye size={12} /> View Details
                          </button>
                          {claim.status === "PENDING" && (
                            <>
                              <button
                                onClick={() => handleClaimStatusUpdate(claim.id, "APPROVED")}
                                disabled={claimActionLoading}
                                className="text-xs font-bold py-1.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-emerald-600/10"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => {
                                  setClaimRejectingId(claim.id);
                                  setClaimRejectReason('');
                                  setShowRejectClaimModal(true);
                                }}
                                disabled={claimActionLoading}
                                className="text-xs font-bold py-1.5 px-3 bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-350 rounded-lg transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-2xs"
                              >
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Claims Pagination */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-505 font-medium border-t border-gray-100 pt-5 mt-6 select-none">
            <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
              {claimsTotalItems === 0 ? (
                'No records available'
              ) : (
                `Showing ${claimsPage * claimsPageSize + 1} to ${Math.min((claimsPage + 1) * claimsPageSize, claimsTotalItems)} of ${claimsTotalItems} claim requests`
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
              <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => fetchClaims()}
                  disabled={claimsLoading}
                  title="Reload current page"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:text-[#2ab391] hover:border-[#2ab391]/40 hover:bg-teal-50/50 rounded-xl font-bold text-xs transition-all cursor-pointer shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCw size={12} className={claimsLoading ? 'animate-spin' : ''} />
                  Reload
                </button>
                <span className="text-xs text-gray-550 font-bold uppercase tracking-wider">Show</span>
                <div className="relative">
                  <select
                    value={claimsPageSize}
                    onChange={(e) => {
                      setClaimsPageSize(Number(e.target.value));
                      setClaimsPage(0);
                    }}
                    className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                    <ChevronDown size={12} />
                  </span>
                </div>
                <span className="text-xs text-gray-550 font-bold uppercase tracking-wider">Entries</span>
              </div>

              {claimsTotalPages > 0 && (
                <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setClaimsPage(p => Math.max(0, p - 1))}
                    disabled={claimsPage === 0}
                    className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
                  >
                    Previous
                  </button>

                  <div className="hidden sm:flex items-center gap-1">
                    {getPageNumbers(claimsPage, claimsTotalPages).map((pageNum, idx) => {
                      if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                        return (
                          <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                            ...
                          </span>
                        );
                      }
                      const isActive = claimsPage === pageNum;
                      return (
                        <button
                          key={`page-${pageNum}`}
                          type="button"
                          onClick={() => setClaimsPage(pageNum)}
                          className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${isActive
                            ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                            : 'bg-white border-gray-200 text-gray-750 hover:bg-gray-50'
                            }`}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    })}
                  </div>

                  <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-xs min-w-[70px] text-center">
                    {claimsPage + 1} / {claimsTotalPages}
                  </span>

                  <button
                    type="button"
                    onClick={() => setClaimsPage(p => Math.min(claimsTotalPages - 1, p + 1))}
                    disabled={claimsPage >= claimsTotalPages - 1}
                    className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Reject Claim Modal */}
      {showRejectClaimModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div>
                <span className="text-[10px] uppercase font-bold text-rose-600 tracking-wider">Claim Administration</span>
                <h3 className="text-lg font-bold text-gray-800 mt-1">Reject Ownership Claim</h3>
              </div>
              <button
                onClick={() => {
                  setShowRejectClaimModal(false);
                  setClaimRejectReason('');
                  setClaimRejectingId(null);
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (claimRejectReason.trim().length === 0) {
                  toast.warning("Please provide a rejection reason.");
                  return;
                }
                handleClaimStatusUpdate(claimRejectingId, 'REJECTED', claimRejectReason.trim());
              }}
              className="p-6 space-y-4 text-left"
            >
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Rejection Reason</label>
                <textarea
                  required
                  rows={4}
                  placeholder="e.g. Provided business registration documents do not match the listing name."
                  value={claimRejectReason}
                  onChange={(e) => setClaimRejectReason(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 focus:outline-none transition-all font-semibold text-gray-850"
                />
                <p className="text-[10px] text-gray-450 mt-1 leading-relaxed">
                  Please specify a clear reason for the claim rejection. This explanation will be displayed to the claimant in their vendor dashboard.
                </p>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectClaimModal(false);
                    setClaimRejectReason('');
                    setClaimRejectingId(null);
                  }}
                  disabled={claimActionLoading}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={claimActionLoading}
                  className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-rose-600/10 cursor-pointer"
                >
                  {claimActionLoading ? "Rejecting..." : "Reject Claim"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Claim Details Modal */}
      {selectedClaim && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto">
          <div className="bg-white max-w-5xl w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[90vh] md:max-h-[85vh] my-auto">
            <div className="py-4 px-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0">
              <div>
                <span className="text-[10px] uppercase font-bold text-[#2ab391] tracking-wider">Claim Comparison Details</span>
                <h3 className="text-lg font-bold text-gray-800 mt-1">Claim Request #{selectedClaim.id}</h3>
              </div>
              <button onClick={handleCloseClaimDetails} className="text-gray-400 hover:text-gray-655 p-1 rounded-lg hover:bg-gray-100 transition-colors">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 text-left space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200 gap-y-8 gap-x-12">
                
                {/* Left Column: Existing Listing Details */}
                <div className="space-y-4 md:pr-8 pb-6 md:pb-0">
                  <h4 className="text-sm font-bold text-gray-800 border-b border-gray-100 pb-2 flex items-center gap-1.5 text-left">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2ab391]"></span>
                    Existing Listing Details
                  </h4>

                  {selectedClaimListingLoading ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-450">
                      <span className="w-6 h-6 border-2 border-[#2ab391] border-t-transparent rounded-full animate-spin mb-2"></span>
                      <p className="text-xs font-semibold">Loading listing details...</p>
                    </div>
                  ) : selectedClaimListing ? (
                    <div className="grid grid-cols-1 gap-y-5 text-xs text-gray-500 font-medium">
                      <div>
                        <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Company Name</span>
                        <span className="text-gray-800 font-bold text-xs">{selectedClaimListing.companyName || 'N/A'}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6">
                        <div>
                          <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Listing Phone</span>
                          <span className="text-gray-800 font-semibold">{selectedClaimListing.phone || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Listing Email</span>
                          <span className="text-gray-800 font-semibold text-xs break-all">{selectedClaimListing.email || 'N/A'}</span>
                        </div>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Address</span>
                        <span className="text-gray-800 font-semibold leading-relaxed">
                          {selectedClaimListing.address ? `${selectedClaimListing.address}, ` : ''}
                          {selectedClaimListing.city ? `${selectedClaimListing.city}, ` : ''}
                          {selectedClaimListing.state}
                          {selectedClaimListing.zipcode ? ` - ${selectedClaimListing.zipcode}` : ''}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6">
                        <div>
                          <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Category</span>
                          <span className="text-gray-800 font-semibold">{selectedClaimListing.categoryName || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Established Since</span>
                          <span className="text-gray-800 font-semibold">{selectedClaimListing.establishedSince || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6">
                        <div>
                          <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Website</span>
                          {selectedClaimListing.website ? (
                            <a href={selectedClaimListing.website} target="_blank" rel="noreferrer" className="text-[#2ab391] hover:underline font-semibold break-all">{selectedClaimListing.website}</a>
                          ) : (
                            <span className="text-gray-800 font-semibold">N/A</span>
                          )}
                        </div>
                        <div>
                          <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">GSTIN</span>
                          <span className="text-gray-800 font-semibold">{selectedClaimListing.gstin || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-xs text-gray-400 font-semibold select-none">
                      Failed to load listing details.
                    </div>
                  )}
                </div>

                {/* Right Column: Claim Details */}
                <div className="space-y-4 md:pl-8 pt-6 md:pt-0 text-left">
                  <h4 className="text-sm font-bold text-gray-800 border-b border-gray-100 pb-2 flex items-center gap-1.5 text-left">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                    Claim Request Details
                  </h4>

                  <div className="grid grid-cols-1 gap-y-5 text-xs text-gray-500 font-medium">
                    <div className="grid grid-cols-2 gap-x-6">
                      <div>
                        <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Claimant Name</span>
                        <span className="text-gray-800 font-bold text-xs">{selectedClaim.claimantName || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Claimant Designation</span>
                        <span className="text-gray-800 font-bold text-xs">{selectedClaim.claimantDesignation || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6">
                      <div>
                        <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Claimant Phone</span>
                        <span className="text-gray-800 font-semibold">{selectedClaim.claimantPhone || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Claimant Email</span>
                        <span className="text-gray-800 font-semibold text-xs break-all">{selectedClaim.user_email}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6">
                      <div>
                        <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Listing ID</span>
                        <span className="text-gray-800 font-semibold">{selectedClaim.listing_id}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1 tracking-wide">Submission Date</span>
                        <span className="text-gray-800 font-semibold text-xs">
                          {new Date(selectedClaim.created_at).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-1.5">
                    <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1.5 tracking-wide">Verification Comments</span>
                    <div className="bg-gray-50 p-4 rounded-xl text-gray-655 leading-relaxed text-xs border border-gray-100 whitespace-pre-wrap font-medium max-h-32 overflow-y-auto">
                      {selectedClaim.comments || <span className="italic text-gray-400">No comment provided</span>}
                    </div>
                  </div>

                  {selectedClaim.proofPhotoUrl && (
                    <div className="pt-1.5">
                      <span className="block text-[10px] uppercase text-gray-400 font-bold mb-1.5 tracking-wide">Uploaded Proof Document</span>
                      {selectedClaim.proofPhotoUrl.toLowerCase().endsWith('.pdf') ? (
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col items-center justify-center text-center max-w-xs relative group transition-all hover:border-[#2ab391]">
                          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 border border-rose-100 flex items-center justify-center mb-2 shadow-inner">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="text-[11px] font-bold text-gray-800 mb-2">Proof PDF Document</p>
                          <a
                            href={getImageUrl(selectedClaim.proofPhotoUrl)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 bg-[#2ab391] hover:bg-[#23977a] text-white text-[10px] font-bold px-3.5 py-1.5 rounded-lg shadow-sm transition-all select-none cursor-pointer"
                          >
                            View PDF Document
                          </a>
                        </div>
                      ) : (
                        <div className="relative group max-w-xs rounded-xl overflow-hidden border border-gray-200 p-1 bg-white hover:border-[#2ab391] transition-all flex items-center justify-center cursor-zoom-in">
                          <img
                            src={getImageUrl(selectedClaim.proofPhotoUrl)}
                            alt="Claim proof document"
                            className="w-full h-auto object-contain rounded-lg max-h-48"
                            onClick={() => setLightboxImage(getImageUrl(selectedClaim.proofPhotoUrl))}
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white rounded-lg">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-between items-center gap-3 bg-gray-50/50 flex-shrink-0 select-none">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-550 uppercase">Status:</span>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase border ${selectedClaim.status === "PENDING"
                  ? "bg-orange-50 text-orange-700 border-orange-100"
                  : selectedClaim.status === "APPROVED"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                    : "bg-rose-50 text-rose-700 border-rose-200"
                  }`}>
                  {selectedClaim.status}
                </span>
              </div>

              <div className="flex items-center gap-2">
                {selectedClaim.status === "PENDING" && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        handleClaimStatusUpdate(selectedClaim.id, "APPROVED");
                        handleCloseClaimDetails();
                      }}
                      disabled={claimActionLoading}
                      className="text-xs font-bold py-1.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-emerald-600/10"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setClaimRejectingId(selectedClaim.id);
                        setClaimRejectReason('');
                        setShowRejectClaimModal(true);
                        handleCloseClaimDetails();
                      }}
                      disabled={claimActionLoading}
                      className="text-xs font-bold py-1.5 px-3 bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-350 rounded-lg transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-2xs"
                    >
                      Reject
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={handleCloseClaimDetails}
                  className="px-4 py-2 border border-gray-250 text-gray-650 hover:bg-gray-50 hover:text-gray-800 rounded-lg text-xs font-semibold transition-all active:scale-[0.98] cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimRequestsTab;
